// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// How big to make the drawing buffer.
//
// Pure arithmetic, deliberately separated from the renderer that consumes it, because this is
// the decision that most often goes wrong and it is invisible when it does: nothing crashes,
// the machine is simply blurry on one device and unplayably slow on another, and neither shows
// up in a test that never opened a GPU.

import type { TierBudget } from './tiers'

export interface DrawingBuffer {
  /** Device pixels across and down. Always at least 1 × 1, always integral. */
  readonly width: number
  readonly height: number
  /** The ratio actually used, which is what the renderer must be told. */
  readonly pixelRatio: number
}

/**
 * Resolve a CSS size and a device-pixel ratio against a tier's budget.
 *
 * Three clamps, in order, and the order matters:
 *
 *  1. **Ratio ceiling.** Past roughly 2 the extra fragments buy nothing a player can see.
 *  2. **Pixel budget.** A ratio ceiling alone does not bound the work — a 4K panel at ratio 1
 *     is 8.3 megapixels. Over budget, the ratio is scaled by the square root of the overshoot,
 *     because pixels go as the square of the ratio.
 *  3. **Floor of one device pixel.** A zero-sized buffer is a WebGL error, and a canvas that
 *     is briefly 0 × 0 during a layout pass is completely routine.
 */
export function drawingBufferFor(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  budget: TierBudget,
): DrawingBuffer {
  // A negative or non-finite CSS size means layout has not settled; treat it as the floor
  // rather than propagating NaN into the renderer, where it surfaces as a blank canvas.
  const w = Number.isFinite(cssWidth) && cssWidth > 0 ? cssWidth : 1
  const h = Number.isFinite(cssHeight) && cssHeight > 0 ? cssHeight : 1
  const reported = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1

  let ratio = Math.min(reported, budget.pixelRatioMax)

  const wanted = w * h * ratio * ratio
  if (wanted > budget.pixelBudget) {
    ratio *= Math.sqrt(budget.pixelBudget / wanted)
  }

  const width = Math.max(1, Math.round(w * ratio))
  const height = Math.max(1, Math.round(h * ratio))
  return { width, height, pixelRatio: ratio }
}

/**
 * Has the buffer changed enough to be worth reallocating?
 *
 * Resizing a WebGL drawing buffer reallocates it and every attachment, which is expensive and
 * can drop a frame. A window being dragged by its corner fires a resize event per pixel, so a
 * host that reacted to each one would spend the whole drag reallocating.
 *
 * The filter is the rounding in `drawingBufferFor`, not a tolerance here: a CSS change too
 * small to move a device pixel produces the same integers and is free, and one that does move
 * a device pixel is a real change that must not be swallowed.
 */
export function bufferChanged(a: DrawingBuffer, b: DrawingBuffer): boolean {
  return a.width !== b.width || a.height !== b.height
}
